import { useRef, type KeyboardEvent, type PointerEvent } from "react";
import { motion, useDragControls, type PanInfo } from "motion/react";
import { ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import type { MarketSignal } from "@/types/marketSignal.type";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import {
  OBSERVATION_TYPES,
  citationOf,
  observationEvidenceDetail,
  openSourceLabel,
} from "./evidenceDisplay";
import { formatFullDate, formatShortDate } from "./signalMeta";
import { HOUSE_EASE } from "./signalsMotion";
import { useDialogTrap } from "./SignalsDialog";
import SourceFavicon from "./SourceFavicon";

/** A pull this far down (or a flick this fast) dismisses the sheet. */
const DISMISS_DISTANCE = 120;
const DISMISS_VELOCITY = 500;
/** A horizontal swipe this far, and clearly more across than down, changes source. */
const SWIPE_DISTANCE = 60;

/** The url as a reader checks it: host and path, no scheme, no "www.". */
function displayUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}`.replace(/\/$/, "");
    return `${u.hostname.replace(/^www\./, "")}${path}`;
  } catch {
    return url;
  }
}

/**
 * SourceSheet: one source behind the deck's top card, read on the phone.
 *
 * A bottom sheet over a scrim: the site and a Close, where this source sits in
 * the signal's list with Previous / Next (and a horizontal swipe), then the
 * headline when it has a real one, the finding, where it came from, the url,
 * and a way out named for where it lands. Dismissed by dragging it down from its
 * top edge, by Close, or by Escape; focus is trapped inside and handed back to
 * the prompt that opened it. The board mounts it only while it is open.
 */
export default function SourceSheet({
  signal,
  index,
  reduced,
  onIndexChange,
  onClose,
}: {
  signal: MarketSignal;
  index: number;
  reduced: boolean;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  useDialogTrap(panelRef, onClose);

  const observations = signal.groundingObservations;
  const n = observations.length;
  const at = Math.max(0, Math.min(n - 1, index));
  const observation = observations[at];
  if (!observation) return null;

  const c = citationOf(observation);
  const isCitation = observation.type === OBSERVATION_TYPES.REPORT_CITATION;
  const isAnalysis =
    observation.type === OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS ||
    observation.type === OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS;
  const finding = isCitation ? c.gist : observationEvidenceDetail(observation);
  const provenance = `${
    signal.sourceRunId === null ? "Logged by hand" : "Cited by the field reporter"
  } on ${formatShortDate(c.observedAt)}, behind “${signal.title}”.`;

  const go = (step: -1 | 1) => {
    const next = at + step;
    if (next >= 0 && next < n) onIndexChange(next);
  };

  // The sheet is pulled from its top edge only, so the text below can still scroll.
  const startPull = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    dragControls.start(e);
  };

  const onPullEnd = (_e: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_DISTANCE || info.velocity.y > DISMISS_VELOCITY) onClose();
  };

  const onSwipeEnd = (_e: unknown, info: PanInfo) => {
    const { x, y } = info.offset;
    if (Math.abs(x) < SWIPE_DISTANCE || Math.abs(x) < Math.abs(y) * 1.5) return;
    go(x < 0 ? 1 : -1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    }
  };

  const slide = reduced
    ? { initial: false as const, exit: { opacity: 0, transition: { duration: 0 } } }
    : {
        initial: { y: "100%" },
        exit: { y: "100%", transition: { duration: 0.26, ease: HOUSE_EASE } },
      };

  return (
    <div className="sig-sheet">
      <motion.div
        className="sig-sheet__scrim"
        aria-hidden="true"
        onClick={onClose}
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: reduced ? 0 : 0.2 } }}
      />
      <motion.div
        ref={panelRef}
        className="sig-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sig-sheet-host"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        {...slide}
        animate={{ y: 0 }}
        transition={{ duration: 0.32, ease: HOUSE_EASE }}
        drag="y"
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 1 }}
        onDragEnd={onPullEnd}
      >

        <div className="sig-sheet__pull" onPointerDown={startPull}>
          <span className="sig-sheet__grab" aria-hidden="true">
            <span className="sig-sheet__handle" />
          </span>
          <div className="sig-sheet__row">
            <span className="sig-sheet__site">
              <SourceFavicon host={c.host} size={15} />
              <span className="sig-sheet__host" id="sig-sheet-host">
                {c.host ?? "Source"}
              </span>
            </span>
            <button
              type="button"
              className="sig-icon-btn sig-icon-btn--lg"
              aria-label="Close"
              onClick={onClose}
            >
              <X aria-hidden="true" />
            </button>
          </div>
          <div className="sig-sheet__row sig-sheet__row--pos">
            <span className="sig-sheet__pos">
              Source {at + 1} of {n} · {formatFullDate(c.observedAt)}
            </span>
            <span className="sig-sheet__nav">
              <button
                type="button"
                className="sig-icon-btn sig-icon-btn--lg"
                aria-label="Previous source"
                disabled={at === 0}
                onClick={() => go(-1)}
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="sig-icon-btn sig-icon-btn--lg"
                aria-label="Next source"
                disabled={at >= n - 1}
                onClick={() => go(1)}
              >
                <ChevronRight aria-hidden="true" />
              </button>
            </span>
          </div>
        </div>

        <motion.div
          key={observation.id}
          className="sig-sheet__body"
          onPanEnd={onSwipeEnd}
          style={{ touchAction: "pan-y" }}
        >
          {c.title ? (
            <h3 className="sig-sheet__title" dir="auto">
              {scrubVendorTerms(c.title)}
            </h3>
          ) : null}
          {finding ? (
            isAnalysis ? (
              <div className="sig-sheet__finding sig-md" dir="auto">
                <ChatMarkdown content={finding} />
              </div>
            ) : (
              <p className="sig-sheet__finding" dir="auto">
                {scrubVendorTerms(finding)}
              </p>
            )
          ) : null}
          <p className="sig-sheet__prov" dir="auto">
            {provenance}
          </p>
          {c.url ? (
            <p className="sig-sheet__url" title={c.url}>
              {displayUrl(c.url)}
            </p>
          ) : null}
        </motion.div>

        {c.url ? (
          <div className="sig-sheet__foot">
            <a className="sig-sheet__open" href={c.url} target="_blank" rel="noopener noreferrer">
              {openSourceLabel(c.host)}
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
